import { memo, useEffect, useRef, useState } from 'react'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>
  fallbackDuration: number
}

export const VideoElapsedOverlay = memo(({ videoRef, fallbackDuration }: Props) => {
  const lastSecondRef = useRef(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [browserDuration, setBrowserDuration] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedMetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        setBrowserDuration(video.duration)
      }
    }

    const handleTimeUpdate = () => {
      const nowSec = Math.floor(video.currentTime)
      if (nowSec !== lastSecondRef.current) {
        lastSecondRef.current = nowSec
        setCurrentTime(video.currentTime)
      }
    }

    const handleSeeked = () => {
      setCurrentTime(video.currentTime)
      lastSecondRef.current = Math.floor(video.currentTime)
    }

    const handlePlay = () => setHasStarted(true)

    if (Number.isFinite(video.duration) && video.duration > 0) {
      setBrowserDuration(video.duration)
    }
    if (!video.paused) setHasStarted(true)

    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('play', handlePlay)

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('play', handlePlay)
    }
  }, [videoRef])

  const totalSec = browserDuration || fallbackDuration
  const elapsed = Math.min(totalSec, Math.floor(currentTime))
  const display = hasStarted ? elapsed : totalSec

  return <VideoDuration duration={display} />
})

VideoElapsedOverlay.displayName = 'VideoElapsedOverlay'
